import styled from "@emotion/styled"
import { Button, Divider, Typography } from "@mui/material"
import { useLocale, useTranslations } from "next-intl"
import Image from "next/image"
import { useState } from "react"
import { Desktop, Mobile } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Link } from "utils"
import {
  StringStringKeyValuePair,
  TrafficUpdate,
  TrafficUpdateSinglePage,
} from "utils/transformUpdate"

const StyledContainer = styled.div<{ overflow?: boolean; isLink?: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 24px;
  flex: 1 0 0;
  border-radius: 12px;
  height: max-content;
  background-color: white;
  text-decoration: none;
  box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);
  position: relative;
  ${({ theme }) => theme.breakpoints.up("md")} {
    overflow: ${(props) => (props.overflow ? "hidden" : "visible")};
  }

  ${(props) =>
    props.isLink &&
    `
    cursor: pointer;

    :hover::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 12px;
      border: 1px solid ${sharedColors.labelGray};
      pointer-events: none;
      z-index: 1;
    }
  `}
`

const TopBox = styled.div<{ regularUpdateTag?: boolean }>`
  display: flex;
  padding: 16px;
  width: 100%;
  height: 45px;
  align-items: center;
  background-color: ${(props) =>
    props.regularUpdateTag ? "#EDFAF4" : "transparent"};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 14px;
  padding-top: 0;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 16px;
    padding-top: 0;
  }
`

const StyledTypography = styled(Typography)`
  font-size: 18px !important;
  line-height: 144.444% !important;
  p {
    margin: 0;
  }
`

const DateContainer = styled.div<{ spaceBetween?: boolean }>`
  display: flex;
  gap: 7px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
    justify-content: ${(props) =>
      props.spaceBetween ? "space-between" : "unset"};
  }

  align-items: center;
  width: 100%;
  align-self: center;
  height: 13px;
`

const StyledDivider = styled(Divider)`
  width: 100%;
  height: 1px;
  background-color: #d1d5db;
`

const NumberContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1 0 0;
`

const MobileNumberContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`
const StyledNumber = styled.div`
  display: flex;
  padding: 0px 12px;
  height: 29px;
  justify-content: center;
  align-items: center;
  border-radius: 50px;
  border: 1px solid ${sharedColors.brandDark};
  background: ${sharedColors.white};
`

const ShowMoreButton = styled(Button)`
  width: 130px;
  height: 26px;
  padding: 0px !important;
`

const formatDate = (dateString: string) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  const datePart = date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  })
  const timePart = date.toLocaleString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
  return `${datePart}, ${timePart}`
}

export default function TraffixBox({
  el,
  overflow,
  spaceBetween,
  individual,
}: {
  el: TrafficUpdateSinglePage | TrafficUpdate
  overflow?: boolean
  spaceBetween?: boolean
  individual?: boolean
}) {
  const [showMore, setShowMore] = useState(false)
  const t = useTranslations("Homepage")
  const locale = useLocale()
  // Type guard to check if el is TrafficUpdateSinglePage
  const isSinglePageCheck = (
    obj: TrafficUpdate | TrafficUpdateSinglePage,
  ): obj is TrafficUpdateSinglePage => {
    return "affectedLines" in obj
  }
  const isSinglePage = isSinglePageCheck(el)
  const currentLines: string[] | number[] | StringStringKeyValuePair[] =
    isSinglePage ? el.affectedLines : el.lines

  const showMobileIndividual = showMore
    ? currentLines
    : currentLines.slice(0, 4)
  return (
    <StyledContainer
      as={!individual ? Link : "div"}
      {...(!individual && { href: `/traffic-updates/${el.messageId}` })}
      overflow={overflow}
      isLink={!individual}
    >
      <TopBox regularUpdateTag={!!el.regularUpdateTag}>
        {el.regularUpdateTag && el.causeHe ? (
          <StyledTypography
            component="div"
            color={sharedColors.brandDark}
            dangerouslySetInnerHTML={{ __html: el.regularUpdateTag }}
          />
        ) : null}
        {!el.regularUpdateTag && (
          <StyledTypography>{el.causeHe}</StyledTypography>
        )}
      </TopBox>
      <Content>
        <DateContainer spaceBetween={spaceBetween}>
          <StyledTypography fontWeight={700}>
            {formatDate(el.activePeriodStart)}
          </StyledTypography>
          <Image
            src="/icons/trafficArrow.svg"
            alt="תאריך ושעה"
            width={16}
            height={13}
            style={{
              transform:
                locale === "he" || locale === "ar" ? "unset" : "rotate(180deg)",
            }}
          />

          {el.activePeriodEnd ? (
            <>
              <StyledTypography fontWeight={700}>
                {formatDate(el.activePeriodEnd)}
              </StyledTypography>
            </>
          ) : (
            <StyledTypography fontWeight={700}>שינוי קבוע</StyledTypography>
          )}
        </DateContainer>
        <Typography
          variant="body2"
          sx={
            !individual
              ? {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  maxWidth: "100%",
                }
              : undefined
          }
        >
          {el.headerTextHe || el.descriptionTextHe}
        </Typography>
        <StyledDivider />
        {individual && (
          <>
            <Desktop>
              <NumberContainer>
                <StyledTypography fontWeight={700}>
                  {t("busLines")}
                </StyledTypography>

                {currentLines.map((lineItem, i: number) => (
                  <StyledNumber key={i}>
                    <StyledTypography
                      color={sharedColors.brandDark}
                      fontWeight={700}
                    >
                      {typeof lineItem === "string" ||
                      typeof lineItem === "number"
                        ? lineItem
                        : lineItem.key}
                    </StyledTypography>
                  </StyledNumber>
                ))}
              </NumberContainer>
            </Desktop>
            <Mobile>
              <MobileNumberContainer>
                {" "}
                <NumberContainer>
                  <StyledTypography fontWeight={700}>
                    {t("busLines")}
                  </StyledTypography>

                  {showMobileIndividual.map((lineItem, i: number) => (
                    <StyledNumber key={i}>
                      <StyledTypography
                        color={sharedColors.brandDark}
                        fontWeight={700}
                      >
                        {typeof lineItem === "string" ||
                        typeof lineItem === "number"
                          ? lineItem
                          : lineItem.key}
                      </StyledTypography>
                    </StyledNumber>
                  ))}
                </NumberContainer>
                {currentLines.length > 4 && !showMore ? (
                  <ShowMoreButton
                    variant="secondary"
                    onClick={() => setShowMore(true)}
                  >
                    <Typography
                      variant="body3"
                      lineHeight={"100%"}
                      color={sharedColors.brandDark}
                      fontWeight={700}
                    >
                      הצגת כל הקווים
                    </Typography>
                  </ShowMoreButton>
                ) : null}
              </MobileNumberContainer>
            </Mobile>
          </>
        )}
        {!individual && (
          <NumberContainer>
            <StyledTypography fontWeight={700}>
              {t("busLines")}
            </StyledTypography>

            {currentLines?.length <= 4 ? (
              currentLines.map((lineItem, i: number) => (
                <StyledNumber key={i}>
                  <StyledTypography
                    color={sharedColors.brandDark}
                    fontWeight={700}
                  >
                    {typeof lineItem === "string" ||
                    typeof lineItem === "number"
                      ? lineItem
                      : lineItem.key}
                  </StyledTypography>
                </StyledNumber>
              ))
            ) : (
              <>
                <StyledNumber key={5}>
                  <StyledTypography
                    color={sharedColors.brandDark}
                    fontWeight={700}
                  >
                    5+
                  </StyledTypography>
                </StyledNumber>
              </>
            )}
          </NumberContainer>
        )}
      </Content>
    </StyledContainer>
  )
}
